import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { KeyRound, RefreshCw, Search, X, ShieldCheck, Clock, XCircle, FileSignature, Send, Download } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const STATE_LABELS = {
  verified: { label: 'Verificato', icon: ShieldCheck, className: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
  linked: { label: 'Firmato', icon: FileSignature, className: 'bg-primary/15 text-primary border-primary/30' },
  pending: { label: 'In attesa', icon: Clock, className: 'bg-amber-500/15 text-amber-700 border-amber-500/30' },
  expired: { label: 'Scaduto', icon: XCircle, className: 'bg-muted text-muted-foreground border-border' },
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(d);
  } catch {
    return iso;
  }
};

export default function OtpLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [resending, setResending] = useState({});
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getOtpLogs({ status: statusFilter, q: debounced || undefined, limit: 200 });
      setItems(res.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile caricare i log OTP');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debounced]);

  const counts = useMemo(() => {
    const c = { all: items.length, verified: 0, linked: 0, pending: 0, expired: 0 };
    items.forEach((it) => { c[it.state] = (c[it.state] || 0) + 1; });
    return c;
  }, [items]);

  const handleResend = async (row) => {
    if (!row?.email) return;
    setResending((s) => ({ ...s, [row.temp_id]: true }));
    try {
      // Per sessioni scadute o al limite invii, generiamo una nuova sessione (nuovo temp_id).
      // Altrimenti riusiamo il temp_id esistente (semplice reinvio con lo stesso codice hash).
      const shouldReset = row.state === 'expired' || (row.sent_count || 0) >= 3;
      const tempId = shouldReset
        ? (crypto?.randomUUID ? crypto.randomUUID() : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`)
        : row.temp_id;
      const res = await api.sendContractOtp({
        temp_id: tempId,
        email: row.email,
        customer_name: '',
        contract_number: row.contract_number || '',
        company_id: row.company_id || undefined,
      });
      toast.success(shouldReset ? `Nuovo codice generato e inviato a ${res.email_masked || row.email_masked}` : `Codice re-inviato a ${res.email_masked || row.email_masked}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile rispedire il codice');
    } finally {
      setResending((s) => { const n = { ...s }; delete n[row.temp_id]; return n; });
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await api.exportOtpLogs({ status: statusFilter, q: debounced || undefined });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      a.download = `otp_audit_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success('Export CSV completato');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile esportare il log');
    } finally { setExporting(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="otp-logs-page">
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <KeyRound className="size-6 text-primary" />
            Log Firme OTP
          </h1>
          <p className="text-sm text-muted-foreground">Audit completo dei codici OTP generati per la firma elettronica dei contratti.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || loading || items.length === 0} data-testid="otp-logs-export">
            {exporting ? <RefreshCw className="size-4 mr-1 animate-spin" /> : <Download className="size-4 mr-1" />}
            Esporta CSV
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="otp-logs-refresh">
            <RefreshCw className={`size-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
          </Button>
        </div>
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="otp-logs-stats">
        {[
          { key: 'all',      label: 'Totali',        Icon: KeyRound },
          { key: 'linked',   label: 'Firmati',       Icon: FileSignature },
          { key: 'verified', label: 'Solo verificati', Icon: ShieldCheck },
          { key: 'pending',  label: 'In attesa',     Icon: Clock },
          { key: 'expired',  label: 'Scaduti',       Icon: XCircle },
        ].map(({ key, label, Icon }) => (
          <Card
            key={key}
            className={`cursor-pointer transition-colors ${statusFilter === key ? 'ring-2 ring-primary bg-primary/5' : ''}`}
            onClick={() => setStatusFilter(key)}
            data-testid={`otp-logs-card-${key}`}
          >
            <CardContent className="p-3 flex items-center gap-3">
              <div className="size-9 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                <Icon className="size-4" />
              </div>
              <div>
                <div className="text-lg font-semibold tabular-nums">{counts[key] || 0}</div>
                <div className="text-xs text-muted-foreground">{label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Elenco</CardTitle>
          <CardDescription>Le sessioni OTP sono ordinate dalla più recente. Il codice non è mai memorizzato in chiaro: viene salvato solo l’hash e le ultime 4 cifre per audit.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per email, contratto, azienda, operatore..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                data-testid="otp-logs-search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Pulisci ricerca"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px] h-9" data-testid="otp-logs-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="linked">Firmati</SelectItem>
                <SelectItem value="verified">Verificati (senza contratto)</SelectItem>
                <SelectItem value="pending">In attesa di verifica</SelectItem>
                <SelectItem value="expired">Scaduti</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stato</TableHead>
                  <TableHead>Email cliente</TableHead>
                  <TableHead>Contratto</TableHead>
                  <TableHead>Azienda</TableHead>
                  <TableHead>Operatore</TableHead>
                  <TableHead>Codice</TableHead>
                  <TableHead>Inviato</TableHead>
                  <TableHead>Verificato</TableHead>
                  <TableHead>Scadenza</TableHead>
                  <TableHead className="text-right">Tentativi</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">Caricamento in corso...</TableCell></TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">Nessuna sessione OTP trovata</TableCell></TableRow>
                )}
                {!loading && items.map((it) => {
                  const s = STATE_LABELS[it.state] || STATE_LABELS.pending;
                  const Icon = s.icon;
                  return (
                    <TableRow key={it.id} data-testid={`otp-log-row-${it.id}`}>
                      <TableCell>
                        <Badge variant="outline" className={`gap-1 ${s.className}`}>
                          <Icon className="size-3" />{s.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.email_masked || it.email || '—'}</TableCell>
                      <TableCell>
                        {it.contract_id ? (
                          <Link to={`/contracts/${it.contract_id}`} className="text-primary hover:underline text-sm">
                            {it.contract_number || it.contract_id.slice(0, 8)}
                          </Link>
                        ) : <span className="text-muted-foreground text-sm">—</span>}
                      </TableCell>
                      <TableCell className="text-sm">{it.company_name || '—'}</TableCell>
                      <TableCell className="text-sm">
                        <div>{it.created_by_name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{it.created_by_email || ''}</div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">••{(it.verified_at && it.state !== 'pending') ? '••••' : '——'}</TableCell>
                      <TableCell className="text-xs">{formatDateTime(it.created_at)}</TableCell>
                      <TableCell className="text-xs">{formatDateTime(it.verified_at)}</TableCell>
                      <TableCell className="text-xs">{formatDateTime(it.expires_at)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        <span className={it.attempts > 3 ? 'text-destructive font-semibold' : ''}>{it.attempts || 0}</span>
                        <span className="text-muted-foreground">/5</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {it.state === 'linked' ? (
                          <span className="text-xs text-muted-foreground">Firmato</span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => handleResend(it)}
                            disabled={!!resending[it.temp_id]}
                            data-testid={`otp-resend-${it.id}`}
                            title={it.state === 'expired' ? 'Rigenera un nuovo codice per lo stesso cliente' : 'Reinvia il codice al cliente'}
                          >
                            {resending[it.temp_id] ? <RefreshCw className="size-3.5 mr-1 animate-spin" /> : <Send className="size-3.5 mr-1" />}
                            {it.state === 'expired' ? 'Rigenera' : 'Reinvia'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
